import React, { useState } from 'react';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Company } from '../../../../../hooks/useCompany';
import useDfcSurface from '../../../../../hooks/useDfcSurface';
import useFitnessVillageCopy, {
  useLocalizeMessage,
} from '../../../../../hooks/useFitnessVillageCopy';
import { ChoiceRow, ErrorText } from '../../../../shared/dfcVillage/flow/style';
import { Eyebrow } from '../../../../shared/dfcVillage/style';
import { AuthPanelLayout } from '../../../authenticationForm/Types';
import {
  CardShell,
  Field,
  FieldAdornment,
  FieldLabel,
  FieldWrap,
  Form,
  InlineShell,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  Spinner,
  SubmitButton,
} from '../../../authenticationForm/variants/dfcVillageTheme/style';
import { IFormUIProps } from '../../Types';
import { ConsentLink } from './style';

/** The createAccountFields this presentation draws. Anything else keeps the generic form. */
const SUPPORTED_FIELDS = ['firstName', 'lastName'];

/**
 * True when every field the company's sign-up asks for is one the village form draws. A field it
 * cannot draw would still be required by `getUserSchema`, and a hidden required field would block
 * sign-up — so any other field, or the referral code, falls back to the generic form.
 */
export const canUseDfcVillageSignupForm = (company?: Company): boolean => {
  const fields = (company?.createAccountFields || {}) as Record<string, boolean | undefined>;
  const unsupported = Object.keys(fields).filter(
    key => fields[key] && !SUPPORTED_FIELDS.includes(key)
  );
  return unsupported.length === 0 && !company?.enableReferral;
};

interface Props extends IFormUIProps {
  company?: Company;
  /** "inline" inside a host card (checkout, profile); otherwise its own card. */
  layout?: AuthPanelLayout;
}

/**
 * The second step of "Create account" in the Fitness Village design: names if the company asks
 * for them, a password (email sign-up only), and DFC's consent line. Same form, resolver and
 * submit as every theme — `CompleteProfile` owns them.
 */
const CompleteProfileDfcVillage: React.FC<Props> = ({
  form,
  onSubmit,
  isSignupWithEmailAndPassword,
  isLoading,
  company,
  layout,
}) => {
  const surface = useDfcSurface();
  const copy = useFitnessVillageCopy().signup;
  const localize = useLocalizeMessage();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;
  const fields = company?.createAccountFields;
  const agreed = !!watch('privacy');

  const link = (label: string, url?: string) =>
    url ? (
      <ConsentLink surface={surface} href={url} target="_blank" rel="noopener noreferrer">
        {label}
      </ConsentLink>
    ) : (
      label
    );

  const nameField = (name: 'firstName' | 'lastName', label: string, autoComplete: string) => (
    <FieldWrap>
      <FieldLabel surface={surface} htmlFor={`dfc-signup-${name}`}>
        {label}
      </FieldLabel>
      <Field
        surface={surface}
        hasError={!!errors[name]}
        id={`dfc-signup-${name}`}
        autoComplete={autoComplete}
        aria-invalid={!!errors[name]}
        aria-describedby={errors[name] ? `dfc-signup-${name}-error` : undefined}
        {...register(name)}
      />
      {errors[name] && (
        <ErrorText id={`dfc-signup-${name}-error`}>
          {localize(errors[name]?.message ?? '')}
        </ErrorText>
      )}
    </FieldWrap>
  );

  const passwordField = (
    name: 'password' | 'passwordConfirm',
    label: string,
    visible: boolean,
    toggle: () => void
  ) => (
    <FieldWrap>
      <FieldLabel surface={surface} htmlFor={`dfc-signup-${name}`}>
        {label}
      </FieldLabel>
      <Field
        surface={surface}
        hasError={!!errors[name]}
        id={`dfc-signup-${name}`}
        dir="ltr"
        type={visible ? 'text' : 'password'}
        autoComplete="new-password"
        aria-invalid={!!errors[name]}
        aria-describedby={errors[name] ? `dfc-signup-${name}-error` : undefined}
        data-testid={name === 'password' ? 'password-input' : 'password-confirm-input'}
        {...register(name)}
      />
      <FieldAdornment
        surface={surface}
        type="button"
        aria-label={visible ? copy.hidePassword : copy.showPassword}
        onClick={toggle}
      >
        {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
      </FieldAdornment>
      {errors[name] && (
        <ErrorText id={`dfc-signup-${name}-error`}>
          {localize(errors[name]?.message ?? '')}
        </ErrorText>
      )}
    </FieldWrap>
  );

  const body = (
    <PanelBody inline={layout === 'inline'} data-testid="dfc-complete-profile">
      <PanelHeader>
        <Eyebrow tone={surface.accentText}>{copy.eyebrow}</Eyebrow>
        <PanelTitle surface={surface}>
          {isSignupWithEmailAndPassword ? copy.titleEmail : copy.titleSocial}
        </PanelTitle>
        <PanelSubtitle surface={surface}>
          {isSignupWithEmailAndPassword ? copy.bodyEmail : copy.bodySocial}
        </PanelSubtitle>
      </PanelHeader>

      <Form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="profile-form">
        {fields?.firstName && nameField('firstName', copy.firstName, 'given-name')}
        {fields?.lastName && nameField('lastName', copy.lastName, 'family-name')}
        {isSignupWithEmailAndPassword && (
          <>
            {passwordField('password', copy.password, showPassword, () =>
              setShowPassword(!showPassword)
            )}
            {passwordField('passwordConfirm', copy.passwordConfirm, showConfirm, () =>
              setShowConfirm(!showConfirm)
            )}
          </>
        )}

        <ChoiceRow surface={surface} checked={agreed}>
          <input
            type="checkbox"
            data-testid="privacy-checkbox"
            aria-invalid={!!errors.privacy}
            aria-describedby={errors.privacy ? 'dfc-signup-privacy-error' : undefined}
            {...register('privacy')}
          />
          <span>
            {copy.consentLead} {link(copy.privacyNotice, company?.footerPagesLinks?.privacy)}{' '}
            {copy.and} {link(copy.termsOfUse, company?.footerPagesLinks?.terms)}.
          </span>
        </ChoiceRow>
        {errors.privacy && (
          <ErrorText
            id="dfc-signup-privacy-error"
            role="alert"
            data-testid="privacy-checkbox-error"
          >
            {localize(errors.privacy.message ?? '')}
          </ErrorText>
        )}

        <SubmitButton
          surface={surface}
          type="submit"
          disabled={isLoading}
          aria-busy={isLoading}
          data-testid="create-profile-button"
        >
          {isLoading ? <Spinner surface={surface} /> : copy.submit}
        </SubmitButton>
      </Form>
    </PanelBody>
  );

  if (layout === 'inline') return <InlineShell>{body}</InlineShell>;

  return (
    <CardShell surface={surface} data-testid="dfc-complete-profile-card">
      {body}
    </CardShell>
  );
};

export default CompleteProfileDfcVillage;
